import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import {
  ContentActions,
  APPROVE_ACTION_COLORS,
  REJECT_ACTION_COLORS,
  EDIT_ACTION_COLORS,
} from "@/components/ui/ContentActions";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import type { BusinessInformation } from "@/lib/api/review";
import {
  ReviewField,
  FieldRow,
  Divider,
  FieldDecisionActions,
  type FieldDecision,
  type RejectFieldMeta,
  type FieldRejectionData,
  type EditFieldMeta,
  type FieldEditData,
} from "./Shared";
import { FilePreview } from "@/components/ui/FilePreview";

interface BizInfoSectionProps {
  bizInfo: BusinessInformation | undefined;
  fieldDecisions: Record<string, FieldDecision>;
  fieldRejectionData: Record<string, FieldRejectionData>;
  fieldEditData: Record<string, FieldEditData>;
  onDecide: (fieldKey: string, decision: FieldDecision) => void;
  onRejectField: (fieldKey: string, meta: RejectFieldMeta) => void;
  onEditField: (fieldKey: string, meta: EditFieldMeta) => void;
  onUndoDecision: (fieldKey: string) => void;
  onShowReason: (fieldKey: string) => void;
  onShowEdit: (fieldKey: string) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function BusinessInformationSection({
  bizInfo,
  fieldDecisions,
  fieldRejectionData: _fieldRejectionData,
  fieldEditData: _fieldEditData,
  onDecide,
  onRejectField,
  onEditField,
  onUndoDecision,
  onShowReason,
  onShowEdit,
  applicationId,
  t,
}: BizInfoSectionProps) {
  const viewOnly = t("review.review.viewOnly");
  const notProvided = t("review.application.businessInformation.notProvided");
  const notUploaded = t("review.application.legalRepresentative.notUploaded");

  return (
    <View className="items-start w-full">
      {/* Card header */}
      <View className="px-6 py-5 border-b border-stroke-50 items-start w-full">
        <AppText weight="semibold" className="text-lg text-dark-text-50">
          {t("review.application.businessInformation.title")}
        </AppText>
      </View>

      {/* Card body */}
      <View className="px-8 py-6 w-full">
        {/* Company Legal Name (Chinese) — VIEW ONLY, full width */}
        <ReviewField
          label={t("review.application.businessInformation.fields.companyNameChinese")}
          viewOnlyLabel={viewOnly}
        >
          <AppText weight="medium" className="text-lg text-dark-text-50">
            {bizInfo?.companyNameChinese}
          </AppText>
        </ReviewField>

        <Divider />

        {/* Company Legal Name (English) + (Arabic) — with audit actions */}
        <FieldRow>
          <View className="flex-1 gap-3 items-start w-full">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.businessInformation.fields.companyNameEnglish")}
            </AppText>
            <AppText weight="medium" className="text-lg text-dark-text-50">
              {bizInfo?.companyNameEnglish}
            </AppText>
            {fieldDecisions["companyNameEnglish"] ? (
              <FieldDecisionActions
                fieldKey="companyNameEnglish"
                decision={fieldDecisions["companyNameEnglish"]}
                onUndoDecision={onUndoDecision}
                onShowReason={onShowReason}
                onShowEdit={onShowEdit}
                t={t}
              />
            ) : (
              <ContentActions
                actions={[
                  {
                    label: "review.application.approve",
                    onPress: () => onDecide("companyNameEnglish", "approve"),
                    icon: (
                      <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                    ),
                    ...APPROVE_ACTION_COLORS,
                  },
                  {
                    label: "review.application.reject",
                    onPress: () =>
                      onRejectField("companyNameEnglish", {
                        label: t(
                          "review.application.businessInformation.fields.companyNameEnglish"
                        ),
                        value: bizInfo?.companyNameEnglish,
                      }),
                    icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                    ...REJECT_ACTION_COLORS,
                  },
                  {
                    label: "review.application.edit",
                    onPress: () =>
                      onEditField("companyNameEnglish", {
                        label: t(
                          "review.application.businessInformation.fields.companyNameEnglish"
                        ),
                        currentValue: bizInfo?.companyNameEnglish,
                      }),
                    icon: <Icon as={Feather} name="edit-2" size={14} className="text-blue-50" />,
                    ...EDIT_ACTION_COLORS,
                  },
                ]}
              />
            )}
          </View>
          <View className="flex-1 gap-3 items-start w-full">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.businessInformation.fields.companyNameArabic")}
            </AppText>
            {bizInfo?.companyNameArabic ? (
              <AppText weight="medium" className="text-lg text-dark-text-50">
                {bizInfo.companyNameArabic}
              </AppText>
            ) : (
              <AppText className="text-lg text-dark-text-10 italic">{notProvided}</AppText>
            )}
            {fieldDecisions["companyNameArabic"] ? (
              <FieldDecisionActions
                fieldKey="companyNameArabic"
                decision={fieldDecisions["companyNameArabic"]}
                onUndoDecision={onUndoDecision}
                onShowReason={onShowReason}
                onShowEdit={onShowEdit}
                t={t}
              />
            ) : (
              <ContentActions
                actions={[
                  {
                    label: "review.application.edit",
                    onPress: () =>
                      onEditField("companyNameArabic", {
                        label: t("review.application.businessInformation.fields.companyNameArabic"),
                        currentValue: bizInfo?.companyNameArabic,
                      }),
                    icon: <Icon as={Feather} name="edit-2" size={14} className="text-blue-50" />,
                    ...EDIT_ACTION_COLORS,
                  },
                ]}
              />
            )}
          </View>
        </FieldRow>

        <Divider />

        {/* Registered Office Address — VIEW ONLY, full width */}
        <ReviewField
          label={t("review.application.businessInformation.fields.registeredAddress")}
          viewOnlyLabel={viewOnly}
        >
          <AppText weight="medium" className="text-lg text-dark-text-50">
            {bizInfo?.registeredAddress}
          </AppText>
        </ReviewField>

        <Divider />

        {/* City + Postal Code — VIEW ONLY */}
        <FieldRow>
          <ReviewField
            label={t("review.application.businessInformation.fields.city")}
            viewOnlyLabel={viewOnly}
          >
            <AppText weight="medium" className="text-lg text-dark-text-50">
              {bizInfo?.city}
            </AppText>
          </ReviewField>
          <ReviewField
            label={t("review.application.businessInformation.fields.postalCode")}
            viewOnlyLabel={viewOnly}
          >
            <AppText weight="medium" className="text-lg text-dark-text-50">
              {bizInfo?.postalCode}
            </AppText>
          </ReviewField>
        </FieldRow>

        <Divider />

        {/* USSC — with audit actions */}
        <FieldRow>
          <View className="flex-1 gap-3 items-start w-full">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.businessInformation.fields.ussc")}
            </AppText>
            <AppText weight="medium" className="text-lg text-dark-text-50">
              {bizInfo?.ussc}
            </AppText>
            {fieldDecisions["ussc"] ? (
              <FieldDecisionActions
                fieldKey="ussc"
                decision={fieldDecisions["ussc"]}
                onUndoDecision={onUndoDecision}
                onShowReason={onShowReason}
                onShowEdit={onShowEdit}
                t={t}
              />
            ) : (
              <ContentActions
                actions={[
                  {
                    label: "review.application.approve",
                    onPress: () => onDecide("ussc", "approve"),
                    icon: (
                      <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                    ),
                    ...APPROVE_ACTION_COLORS,
                  },
                  {
                    label: "review.application.reject",
                    onPress: () =>
                      onRejectField("ussc", {
                        label: t("review.application.businessInformation.fields.ussc"),
                        value: bizInfo?.ussc,
                      }),
                    icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                    ...REJECT_ACTION_COLORS,
                  },
                ]}
              />
            )}
          </View>
        </FieldRow>

        <Divider />

        {/* Business License */}
        <View className="gap-3 items-start w-full">
          <AppText weight="semibold" className="text-base text-supporting-text-40">
            {t("review.application.businessInformation.fields.businessLicense")}
          </AppText>
          {bizInfo?.businessLicenseUrl ? (
            <FilePreview
              className="max-w-[320px]"
              fileKey={bizInfo?.businessLicenseUrl}
              isVendor={false}
              applicationId={applicationId}
            />
          ) : (
            <AppText className="text-lg text-dark-text-10 italic">{notUploaded}</AppText>
          )}
          {fieldDecisions["businessLicense"] ? (
            <FieldDecisionActions
              fieldKey="businessLicense"
              decision={fieldDecisions["businessLicense"]}
              onUndoDecision={onUndoDecision}
              onShowReason={onShowReason}
              onShowEdit={onShowEdit}
              t={t}
            />
          ) : (
            <ContentActions
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => onDecide("businessLicense", "approve"),
                  icon: (
                    <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                  ),
                  ...APPROVE_ACTION_COLORS,
                },
                {
                  label: "review.application.reject",
                  onPress: () =>
                    onRejectField("businessLicense", {
                      label: t("review.application.businessInformation.fields.businessLicense"),
                      value: bizInfo?.businessLicenseUrl?.split("/").pop(),
                    }),
                  icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                  ...REJECT_ACTION_COLORS,
                },
              ]}
            />
          )}
        </View>
      </View>
    </View>
  );
}
